import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowLeft } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-stone-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-stone-800 hover:text-[#2C5F8D] transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-tight" style={pf}>GoHimalayan</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-stone-400 hover:text-stone-600 transition-colors text-xs">
            <ArrowLeft className="w-3.5 h-3.5" />Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs tracking-[0.3em] uppercase text-stone-400 mb-3">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-3" style={pf}>Terms of Service</h1>
        <p className="text-stone-400 text-sm mb-12">Last updated: April 2026</p>

        <div className="prose prose-stone prose-sm max-w-none space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>1. Acceptance of Terms</h2>
            <p className="text-stone-600 leading-relaxed">By accessing or using GoHimalayan (gohimalayan.com), you agree to be bound by these Terms of Service. If you do not agree, please do not use our platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>2. Description of Service</h2>
            <p className="text-stone-600 leading-relaxed">GoHimalayan is a hospitality technology platform that curates and markets authentic homestays across the Indian Himalayas. We provide discovery, content curation, and direct booking facilitation services. We connect travellers with hosts — we are not a hotel or accommodation provider ourselves.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>3. Booking & Payments</h2>
            <p className="text-stone-600 leading-relaxed">GoHimalayan facilitates direct connections between travellers and hosts via WhatsApp. All bookings, cancellations, and payments are between the traveller and the host. GoHimalayan is not a party to any booking transaction and does not process payments on behalf of hosts or travellers unless explicitly stated.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>4. Content & Accuracy</h2>
            <p className="text-stone-600 leading-relaxed">Property descriptions on GoHimalayan are curated using AI-assisted content generation based on information provided by hosts and publicly available sources. While we strive for accuracy, we do not guarantee that all details (pricing, availability, amenities) are current. Travellers should confirm all details directly with the host before booking.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>5. Intellectual Property</h2>
            <p className="text-stone-600 leading-relaxed">All content on GoHimalayan — including text, design, logos, AI-generated descriptions, and curated imagery — is the property of GoHimalayan or its licensors. You may not reproduce, distribute, or create derivative works without our written permission.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>6. Host/Partner Obligations</h2>
            <p className="text-stone-600 leading-relaxed">Hosts and partners listed on GoHimalayan agree to provide accurate property information, maintain valid licences as required by their state government, respond to traveller inquiries in a timely manner, and honour pricing and availability as listed.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>7. Limitation of Liability</h2>
            <p className="text-stone-600 leading-relaxed">GoHimalayan acts as a technology platform connecting travellers and hosts. We are not liable for the quality, safety, legality, or any other aspect of the accommodation provided by hosts. To the maximum extent permitted by law, GoHimalayan shall not be liable for any indirect, incidental, or consequential damages arising from use of our platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>8. Prohibited Use</h2>
            <ul className="list-disc pl-5 space-y-2 text-stone-600">
              <li>Scraping or automated data collection from GoHimalayan without permission.</li>
              <li>Impersonating a host, traveller, or GoHimalayan representative.</li>
              <li>Using the platform for unlawful purposes.</li>
              <li>Attempting to interfere with the platform's operation or security.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>9. Governing Law</h2>
            <p className="text-stone-600 leading-relaxed">These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in New Delhi, India.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>10. Contact</h2>
            <p className="text-stone-600 leading-relaxed">For questions about these terms, contact us at:</p>
            <div className="mt-3 bg-stone-50 rounded-lg p-4 text-sm text-stone-600">
              <p className="font-semibold text-stone-800">GoHimalayan</p>
              <p>Email: legal@gohimalayan.com</p>
              <p>WhatsApp: +91 9871155963</p>
              <p>Website: gohimalayan.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-100 py-6 px-6 mt-16">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="text-xs text-stone-400">© 2026 GoHimalayan</span>
          <Link to="/privacy" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
}
